import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import Tooltip from '../../../components/ui/Tooltip';

// PRD PM §4.1 — Hedeflerim (Task 26). Gerçek `PeriodGoalController` rotaları
// (grep ile doğrulanmış — brief metnindeki bazı fiil/isimler [PUT vs POST,
// "revision-request" vs "revision"] gerçek koddan sapıyordu, buradaki liste
// controller'ın kendisiyle birebir):
//   GET    v1/period-goals/my?periodId[eq]&status[in]&goalType[eq]
//   GET    v1/period-goals/my/{id}
//   POST   v1/period-goals/my                        (taslak oluştur)
//   PUT    v1/period-goals/my/{id}                    (taslak güncelle)
//   POST   v1/period-goals/my/{id}/submit
//   POST   v1/period-goals/my/key-results/{krId}/progress   (dönem-içi, status="approved")
//   POST   v1/period-goals/my/{id}/kpi-actual                (dönem-içi, status="approved")
//   POST   v1/period-goals/my/{id}/kpi-final                 (dönem-sonu, status="evaluating")
//   POST   v1/period-goals/my/{id}/revision           (flag: GoalRevisionEnabled)
//   POST   v1/period-goals/my/{id}/parent             (flag: CascadingGoalsEnabled)
//   POST   v1/period-goals/my/{id}/employee-scores    (dönem-sonu KR puanları + öz değerlendirme)
//   POST   v1/period-goals/my/{id}/approve-closure     (dönem-sonu kapanış onayı)
//   DELETE v1/period-goals/my/{id}                    (müşteri geri bildirimi 2026-10-28 —
//                                                      yalnız DELETABLE_STATUSES, dönemden bağımsız)
// KR CRUD ayrı bir uç DEĞİL: PeriodGoalRequest.KeyResults dizisi POST/PUT ile
// TAMAMEN yeniden yazılır (mevcut aktif KR'ler soft-delete + gelenler eklenir —
// bkz. PeriodGoalDataAccess.UpdateDraftAsync). GoalEditor bu yüzden KR listesini
// yerel state'te tutup Save'de tam diziyi gönderir.

// EvaluationPeriodFilterRequest yalnız tek-değerli status[eq] destekliyor (status[in]
// yok) — "hedef yazma active'de, puanlama evaluation'da" ihtiyacı için ayrı ayrı
// GET atılıp birleştirilir.
const fetchPeriodsByStatus = async (status) => {
  const res = await api.get('/v1/evaluation-periods', { params: { 'status[eq]': status, pageSize: 50 } });
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

// PMF-10 (Faz 2, 2026-09-11) — HEDEF EKRANLARININ DÖNEM KÜMESİ: TEK KAYNAK.
//
// Kusur: hem MyGoalsPage hem TeamGoalsPage yalnız 'active' + 'evaluation' çekiyordu.
// Değerlendirme penceresi takvimle kapanıp dönem 'calibration'a geçtiği anda (bkz.
// PeriodStateMachine.NextAutoState — geçiş hedeflerin bitmesine DEĞİL takvime bağlı)
// o dönemin hedefleri HER İKİ ekrandan da yok oluyordu: çalışan da yönetici de dönem
// seçicide o dönemi hiç göremiyor, hâlâ okunabilir/kapatılabilir hedeflerine
// erişemiyordu. Backend 'calibration'/'pending_close' dönemlerde hedef OKUMAYA
// (ve kalibrasyon-öncesi kalan kapanış akışlarına) izin vermeye devam ediyordu —
// kayıp tamamen ekran tarafındaydı.
//
// NOT: bu liste GÖRÜNÜRLÜK kümesidir, YAZMA yetkisi DEĞİL. Skor yazan uçlar
// PeriodStateMachine.IsScoringOpen ile ('active'|'evaluation') korunmaya devam eder —
// kalibrasyonda açılan ekran salt-okunur davranır, 409 'scoringClosed' alır.
const GOAL_VISIBLE_PERIOD_STATUSES = ['active', 'evaluation', 'calibration', 'pending_close'];

const fetchGoalVisiblePeriods = async () => {
  const results = await Promise.all(GOAL_VISIBLE_PERIOD_STATUSES.map(fetchPeriodsByStatus));
  return results.flat();
};

// PMB-01 (2026-09-08) — dönem kapanışında hiç onaylanmamış kalan hedefin terminal
// durumu (backend TEK KAYNAK: GoalScoreRules.AbandonedStatus). Bu dosyadaki
// TERMINAL_STATUSES ve inWeightSet bunun üzerinden türetilir; hiçbir yerde çıplak
// 'abandoned' literali tekrarlanmaz.
export const ABANDONED_STATUS = 'abandoned';

// Hedefin ARTIK DEĞİŞTİRİLEMEZ olduğu durumlar. Backend'in mutasyon kapısı
// (IsPeriodLive + durum kapıları) bu iki durumda her yazmayı 409 ile reddeder;
// UI de bu hedeflerde canlı kontrol GÖSTERMEMELİ. PMB-01'den önce yalnız
// "closed" vardı ve kapılar tek tek `status === 'closed'` karşılaştırmalarıyla
// yazılıyordu — yeni "abandoned" durumu o kapıları sessizce yeniden AÇTI
// (bkz. GoalEditor "Üst Hedefe Hizala" bölümü).
export const TERMINAL_STATUSES = ['closed', ABANDONED_STATUS];
export const isTerminalGoalStatus = (status) => TERMINAL_STATUSES.includes(status);

// Müşteri geri bildirimi (2026-10-28) — "Dönem kapanmış ve onayda bekleyen bir hedef için
// hiçbir işlem yapılamıyor… Silinmesine de izin verilmiyor." Silinebilir hedef durumları —
// backend TEK KAYNAK: GoalLifecycleRules.IsDeletableStatus (taahhüt olmamış + terk edilmiş).
// Taahhüt olmuş hedef (approved|evaluating|closed) skor girdisidir, silinmez (409
// performance.goal.deleteNotAllowed). Dönem durumu BİLEREK sorulmaz — askıda kalan hedef tam
// da kapalı/iptal/kalibrasyondaki dönemde durur.
export const DELETABLE_STATUSES = ['draft', 'manager_approval', 'hr_approval', 'revision_required', ABANDONED_STATUS];
export const isDeletableGoalStatus = (status) => DELETABLE_STATUSES.includes(status);

// Müşteri geri bildirimi (2026-10-28) — "hedef satırına tıklandığında kimde onayda beklediği
// görünmeli". Backend YALNIZ tekil okumada (GET my/{id}, GET {id}) doldurur
// (PeriodGoalResponse.PendingApproval*): subject goal|revision, stage manager|hr, ad listesi
// (en fazla 5) + toplam, yönetici çözülemediğinde İK fallback bayrağı. Liste satırlarında bu
// alanlar hep boştur → null döner. Metin: "Onay Bekleyen: Ayşe Yılmaz (Yönetici)" /
// "Onay Bekleyen: A, B +3 (İK)". Kırpılan kişi sayısı dilden bağımsız "+N" ile yazılır.
export const buildPendingApproverText = (goal, t) => {
  if (!goal?.pendingApprovalStage) return null;
  const names = goal.pendingApproverNames || [];
  const total = goal.pendingApproverTotal ?? names.length;
  const extra = Math.max(0, total - names.length);
  const who = names.length > 0
    ? `${names.join(', ')}${extra > 0 ? ` +${extra}` : ''}`
    : t('performance.goal.pendingApprover.unresolved');
  let stage;
  if (goal.pendingApprovalStage === 'flow') {
    // Onay Akışları > Hedef (2026-10-28) — "Onay Akışı · Adım 2/3"; adım akıştan çıkarılmışsa
    // yalnız "Onay Akışı". Adım kimseye çözülemiyorsa karar İK'dadır → "· İK" eklenir.
    stage = goal.pendingApprovalStepIndex != null && goal.pendingApprovalStepCount != null
      ? t('performance.goal.pendingApprover.stage.flowStep', {
        step: goal.pendingApprovalStepIndex, total: goal.pendingApprovalStepCount,
      })
      : t('performance.goal.pendingApprover.stage.flow');
    if (goal.pendingApproverIsHrFallback) stage = `${stage} · ${t('performance.goal.pendingApprover.stage.hr')}`;
  } else if (goal.pendingApprovalStage === 'hr') stage = t('performance.goal.pendingApprover.stage.hr');
  else if (goal.pendingApproverIsHrFallback) stage = t('performance.goal.pendingApprover.hrFallback');
  else stage = t('performance.goal.pendingApprover.stage.manager');
  const label = goal.pendingApprovalSubject === 'revision'
    ? t('performance.goal.pendingApprover.revisionLabel')
    : t('performance.goal.pendingApprover.label');
  return `${label}: ${who} (${stage})`;
};

// Yeni sistemin 7 hedef durumu (bkz. backend GoalStateMachine): draft |
// manager_approval | hr_approval | approved | revision_required | evaluating | closed.
// + terminal "abandoned" (PMB-01): taahhüt sayılmadığı için skora girmez ve
// "kapandı" YEŞİLİNİ de almaz — taslakla aynı nötr slate ailesinden, ondan bir ton
// koyu (kalıcı/terminal olduğu okunsun diye).
// Renk paleti evaluation-periods/config'teki STATUS_COLORS deseniyle tutarlı — bu dosyada
// da (o dosyanın kendi StatusBadge/STATUS_COLORS'ını sayfaya export ettiği desenle
// birebir) DataTable hücre render'ı için burada tutulur.
export const STATUS_COLORS = {
  draft: { color: '#64748B', bg: '#F1F5F9' },
  manager_approval: { color: '#D97706', bg: '#FFFBEB' },
  hr_approval: { color: '#D97706', bg: '#FFFBEB' },
  revision_required: { color: '#C0501A', bg: '#FCE8D5' },
  approved: { color: '#0291EF', bg: '#EFF6FF' },
  evaluating: { color: '#7C3AED', bg: '#F5F3FF' },
  closed: { color: '#16A34A', bg: '#DCFCE7' },
  abandoned: { color: '#475569', bg: '#E2E8F0' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// Skorlama Faz F, Task 5 (spec §4) — bayat hedef rozeti. Komşu rozet deseninin
// (StatusBadge — pastel arkaplan + doygun metin rengi, DÜZ, gradyansız pil
// biçimi; AlignedOrgGoalBadge/DueBadge de AYNI aile) BİREBİR tekrarı —
// modülde (period-goals, performance-inbox, performance-settings) gradyan
// kullanan TEK bir komşu rozet YOK, bu yüzden burada da icat edilmedi. Ton,
// STATUS_COLORS'ın manager_approval/hr_approval ("dikkat gerektirir") amber
// çiftiyle AYNI (#D97706/#FFFBEB) — bayatlık da tam olarak bu anlamı taşıyor.
export const StaleBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #FFFBEB;
  color: #D97706;
`;

// Faz J (spec 2026-08-08-pm-hijyen-faz-j-design.md §4, J3, Task 4) — "Alt
// ekip" rozeti: PeriodGoalResponse.IsSkipLevel satırlarında (yalnız "Ekip
// Hedefleri" — filtresiz İZLEME sekmesi, task-3-report.md §1: karar-bağlamı
// SEKMELERİ hiç skip-level'e AÇILMAZ). StaleBadge'in AYNI görsel dili —
// nötr/bilgilendirici ton (uyarı DEĞİL, `STATUS_COLORS.draft` griyle AYNI).
export const SkipLevelBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #F1F5F9;
  color: #64748B;
`;

// isStale===true bir satır için gösterilecek gün sayısı — BE yalnız booleanı
// (`isStale`) + ham `lastProgressAt`'i verir (task-3-report.md §1.1: "FE
// gösterim hesabı"); BE'nin kendi `IsGoalStale` formülüyle AYNI floor()
// kuralı (task-3-report.md §2) — gün farkının KESRİ atılır, YUVARLANMAZ.
export const staleDaysSince = (lastProgressAt) => {
  if (!lastProgressAt) return null;
  const then = new Date(lastProgressAt).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
};

// Task 26 fix (Important #3) — hedef listesi standart DataTable bileşenine taşındı
// (MyDevelopmentPlansPage'in self-servis DataTable kullanım deseni birebir örnek
// alındı: hook queryParams alır, gerçek backend sayfalama/filtre parametreleriyle
// GET atar, kolonları kendi üretir; sayfa yalnız ek kolon [aksiyonlar] ekler).
const usePeriodGoalsApi = (periodId, queryParams) => {
  const { t } = useTranslation();

  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  // PMF-10 — bkz. fetchGoalVisiblePeriods yorumu (calibration/pending_close dahil).
  const fetchPeriods = useCallback(async () => {
    setPeriodsFetching(true);
    try {
      setPeriods(await fetchGoalVisiblePeriods());
    } catch {
      setPeriods([]);
    } finally {
      setPeriodsFetching(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const [goals, setGoals] = useState([]);
  const [goalsFetching, setGoalsFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Plan 3, Task 8 — kapı-3 (performance.goal.weightSumInvalid, 409) yakalanan son
  // Data ({currentSum, goalCount}); MyGoalsPage'in kalıcı ağırlık bandını EK olarak
  // "flaşlaması" için (interceptor toast'u zaten anahtar metnini basıyor, bkz.
  // approveClosure yorumu — bu yalnız bağlamsal EKLEME). Her yeni liste yüklemesinde
  // (aşağıdaki effect) temizlenir — "flash" bir sonraki gerçek veri tazelemesine kadar
  // yaşar.
  const [weightGateError, setWeightGateError] = useState(null);

  useEffect(() => {
    if (!periodId) {
      setGoals([]);
      setPagination({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
      return;
    }
    setWeightGateError(null);
    const params = {
      pageNumber: 1, pageSize: 10, ...queryParams, 'periodId[eq]': periodId,
    };
    setGoalsFetching(true);
    api.get('/v1/period-goals/my', { params })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setGoals(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setGoals([]))
      .finally(() => setGoalsFetching(false));
  }, [periodId, queryParams, refreshTrigger]);

  // Müşteri geri bildirimi (2026-10-28) — "Hedeflerim listesinde hedefin ağırlığı gözükmeli".
  // Kolon, ekip ekranının ağırlık kolonuyla (buildTeamGoalColumns) AYNI koşulda ve AYNI biçimde
  // çizilir: yalnız seçili dönem usesGoalWeights iken (parite — bayraksız dönemde kolon HİÇ
  // yok, ağırlık bandı ve GoalEditor alanlarıyla aynı kural); değer "40%", boşsa "-".
  const periodUsesGoalWeights = useMemo(
    () => !!periods.find((p) => p.id === periodId)?.usesGoalWeights,
    [periods, periodId],
  );

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'title',
      label: t('performance.goal.fields.title'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'goalType',
      label: t('performance.goal.fields.goalType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.goal.goalType.${v}`, { defaultValue: v }),
      },
    },
    ...(periodUsesGoalWeights ? [{
      name: 'weightPct',
      label: t('performance.goal.fields.weight'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => <span data-role="weight-cell">{v != null ? `${v}%` : '-'}</span>,
      },
    }] : []),
    {
      name: 'completionPct',
      label: t('performance.goal.kpiCompletionPct'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (v != null ? `${v}%` : '-'),
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: false, sort: false,
        // Skorlama Faz F, Task 5 — bayat rozet, row lookup deseni
        // buildTeamGoalColumns'ın weightPct hücresiyle (config/index.js,
        // aşağıda) BİREBİR aynı: `goals` closure'ından tableMeta.rowIndex ile.
        customBodyRender: (v, tableMeta) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          const row = goals[tableMeta.rowIndex];
          const staleDays = row?.isStale ? staleDaysSince(row.lastProgressAt) : null;
          return (
            <>
              <StatusBadge color={cfg.color} bg={cfg.bg}>
                {t(`performance.goal.status.${v}`, { defaultValue: v })}
              </StatusBadge>
              {staleDays != null && (
                <StaleBadge data-role="stale-badge">
                  {t('staleGoals.badge', { days: staleDays })}
                </StaleBadge>
              )}
            </>
          );
        },
      },
    },
  ], [t, goals, periodUsesGoalWeights]);

  const getById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/period-goals/my/${id}`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  // Müşteri geri bildirimi (2026-10-28) — kendi hedefini silme (DELETE my/{id}). Diğer /my
  // aksiyonlarıyla AYNI boolean sözleşme; başarı toast'unu interceptor basar
  // (performance.goal.deleted), 409 deleteNotAllowed'ı da öyle.
  const deleteGoal = useCallback(async (id) => {
    try {
      await api.delete(`/v1/period-goals/my/${id}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  const createDraft = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/period-goals/my', payload);
      return res.data?.data || true;
    } catch {
      return null;
    }
  }, []);

  const updateDraft = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/period-goals/my/${id}`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  const submitGoal = useCallback(async (id) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/submit`);
      return true;
    } catch {
      return false;
    }
  }, []);

  // #71 (UI düzeltme demeti, spec 2026-08-09, T4) — Hedeflerim toplu onaya
  // gönderme. Tekil submitGoal'ün (boolean) AKSİNE gerçek yanıt gövdesini
  // (SubmitBulkGoalsResponse: {submitted, failed}) döner — MyGoalsPage kısmi
  // başarı sonucunu (başarı sayısı + satır-bazlı başarısızlık nedeni) GÖSTERMEK
  // zorunda, tek bir boolean yetmez (BulkAssignModal'ın bulkAssignGoals
  // deseniyle AYNI: createDraft/bulkAssignGoals gibi Data'yı OLDUĞU GİBİ
  // döner, hata durumunda null — sınır hataları [bulkTargetRequired/
  // bulkLimitExceeded] interceptor'ın kendi toast'ıyla zaten bildirilir).
  const submitBulkGoals = useCallback(async (goalIds) => {
    try {
      const res = await api.post('/v1/period-goals/my/submit-bulk', { goalIds });
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  // Skorlama Faz F, Task 5 (task-1-report.md §1) — `note?` additive gövde
  // alanı: her başarılı yazımda BE bir GoalCheckIn satırı üretir (Old/New +
  // opsiyonel not). `note` verilmezse (undefined) davranış AYNEN eskisi gibi.
  const updateKrProgress = useCallback(async (keyResultId, progressPct, note) => {
    try {
      await api.post(`/v1/period-goals/my/key-results/${keyResultId}/progress`, { progressPct, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Dönem-içi (status="approved") KPI gerçekleşen değeri takibi.
  const updateKpiActual = useCallback(async (id, value, note) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/kpi-actual`, { value, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Dönem-sonu (status="evaluating") KPI final gerçekleşen değeri.
  // P1 — deviationRationale (opsiyonel sapma gerekçesi): undefined → gövdeye
  // girmez (BE null=dokunma semantiği), '' → bilinçli temizleme.
  const submitFinalKpi = useCallback(async (id, value, deviationRationale) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/kpi-final`, { value, deviationRationale });
      return true;
    } catch {
      return false;
    }
  }, []);

  // changes: { Title?, Description?, KpiTargetValue?, "kr:<id>"?: string } — yalnız
  // GERÇEKTEN değişen alanlar gönderilir (beyaz liste dışı alan 400 döner).
  const requestRevision = useCallback(async (id, changes) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/revision`, { changes });
      return true;
    } catch {
      return false;
    }
  }, []);

  const setParent = useCallback(async (id, parentGoalId) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/parent`, { parentGoalId });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Skorlama Plan 4, Task 4 (task-4-brief.md) — "Üst Hedefe Hizala" seçici.
  // Gerçek uç task-3-report.md'de doğrulanmış: PUT v1/period-goals/{id}/aligned-org-goal
  // gövde { alignedOrgGoalId }. alignedOrgGoalId=null mevcut hizalamayı kaldırır
  // (SetParentGoalRequest ile AYNI null-temizler sözleşmesi, setParent'ın hemen
  // üstündeki desenin birebir aynısı).
  const setAlignedOrgGoal = useCallback(async (id, alignedOrgGoalId) => {
    try {
      await api.put(`/v1/period-goals/${id}/aligned-org-goal`, { alignedOrgGoalId: alignedOrgGoalId || null });
      return true;
    } catch {
      return false;
    }
  }, []);

  // P1 — deviationRationale: submitFinalKpi ile aynı opsiyonel semantik.
  const submitEmployeeScores = useCallback(async (id, scores, selfAssessmentText, deviationRationale) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/employee-scores`, { scores, selfAssessmentText, deviationRationale });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Plan 3, Task 3 kapı-3 (performance.goal.weightSumInvalid, 409, Data:
  // {currentSum, goalCount}) burada da tetiklenebilir — dış sözleşme (boolean dönüş)
  // GoalEditor'ın mevcut `const ok = await apiHooks.approveClosure(...)` çağrısıyla
  // BİREBİR aynı kalır (GoalEditor'a bu görevde DOKUNULMADI); kapı hatasının
  // Data'sı yalnız weightGateError state'ine yazılır — MyGoalsPage aynı hook
  // örneğini paylaştığı için (apiHooks bu hook'un fonksiyonlarıdır) state
  // güncellemesi MyGoalsPage'i yeniden render eder, GoalEditor'dan çağrılmış olsa
  // bile.
  const approveClosure = useCallback(async (id) => {
    try {
      await api.post(`/v1/period-goals/my/${id}/approve-closure`);
      return true;
    } catch (err) {
      if (err.response?.data?.message === 'performance.goal.weightSumInvalid') {
        setWeightGateError(err.response.data.data || null);
      }
      return false;
    }
  }, []);

  // Plan 3, Task 8 — "Ağırlıkları Eşitle" self-servis ucu (bkz. IPeriodGoalService.
  // EqualizeWeightsAsync yorumu): periodId QUERY STRING, gövde yok. Yanıt
  // Data: GoalWeightItem[] ({id, weightPct}) — MyGoalsPage çağrı sonrası
  // TAM listeyi yeniden çeker (refresh()), bu yüzden dönen dizi burada state'e
  // YAZILMIYOR, yalnızca başarı/başarısızlık raporlanıyor (diğer tüm /my
  // aksiyonlarıyla AYNI boolean sözleşme).
  const equalizeWeights = useCallback(async (periodIdParam) => {
    try {
      await api.post('/v1/period-goals/equalize-weights', null, { params: { periodId: periodIdParam } });
      return true;
    } catch {
      return false;
    }
  }, []);

  // P3 (2026-10-08) — kilometre taşı: adım "tamamlandı" bildirimi (kanıt notu ve/veya belge
  // bağlantısı). Yalnız hedef sahibi; hedef approved|evaluating. Kabul yöneticinin işidir.
  const submitMilestoneStep = useCallback(async (goalId, stepId, payload) => {
    try {
      await api.post(`/v1/period-goals/my/${goalId}/milestone-steps/${stepId}/submit`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  // P3 — eşik/ikili: dönem içi sonuç (approved). payload: { value? | met?, note? }.
  const updateThresholdResult = useCallback(async (goalId, payload) => {
    try {
      await api.post(`/v1/period-goals/my/${goalId}/threshold-result`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  // P3 — eşik/ikili: dönem sonu final sonuç (evaluating). payload: { value? | met?, deviationRationale? }.
  const submitFinalThreshold = useCallback(async (goalId, payload) => {
    try {
      await api.post(`/v1/period-goals/my/${goalId}/threshold-final`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    periods,
    periodsFetching,
    goals,
    goalsFetching,
    pagination,
    columns,
    refresh,
    getById,
    deleteGoal,
    createDraft,
    updateDraft,
    submitGoal,
    submitBulkGoals,
    updateKrProgress,
    updateKpiActual,
    submitFinalKpi,
    requestRevision,
    setParent,
    setAlignedOrgGoal,
    submitEmployeeScores,
    approveClosure,
    equalizeWeights,
    weightGateError,
    submitMilestoneStep,
    updateThresholdResult,
    submitFinalThreshold,
  };
};

// Plan 3, Task 7 — Ağırlık (%) alanı form⇄payload dönüşümü: hedef (PeriodGoalRequest/
// Response.WeightPct) VE her KR satırı (KeyResultInput/Response.WeightPct) AYNI opsiyonel-
// ondalık kuralını izler — GoalEditor'daki kpiTargetValue eşlemesinin (buildInitialForm/
// buildPayload) birebir aynı deseni, iki çağrı noktasında (hedef + her KR) tekrarlanmasın
// diye burada TEK yerde tutulur. Boş string ⇄ undefined/null, dolu ⇄ Number. Dönem
// UsesGoalWeights=false iken alanlar hiç render edilmediğinden form değeri hep '' kalır →
// payload'da hep undefined (sert aralık/bayrak doğrulaması BE'de, bkz. PeriodGoalDataAccess.
// ValidateWeight).
export const weightPctToForm = (v) => (v != null ? String(v) : '');
export const weightPctToPayload = (v) => (v !== '' && v != null ? Number(v) : undefined);

// Yalnız draft|revision_required durumundaki hedef tam olarak düzenlenebilir
// (başlık/açıklama/KR listesi/KPI alanları). Diğer tüm durumlar salt-okunur
// başlık alanları + duruma özgü aksiyonlar (ilerleme/gerçekleşen/puanlama).
export const EDITABLE_STATUSES = ['draft', 'revision_required'];

// KR/KPI dönem-içi takibi (progress, kpi-actual) yalnız "approved" durumda açık.
export const TRACKING_STATUS = 'approved';

// Dönem sonu puanlama/kapanış yalnız "evaluating" durumda açık.
export const EVALUATING_STATUS = 'evaluating';

// Plan 3, Task 8 — "Ağırlıkları Eşitle" self-servis ucu backend'de
// IsGoalWritingOpen (active|evaluation) kapısını kullanır (bkz.
// EqualizeWeightsAsync yorumu) — MyGoalsPage'in kendi periods listesi zaten
// yalnız active+evaluation dönemlerini çeker (fetchPeriodsByStatus çifti),
// bu yüzden burada AYNI iki durumu tekrar sabitliyoruz: canCreateGoal'ın
// (yalnız "active", hedef OLUŞTURMA için) tersine, ağırlık eşitleme MEVCUT
// hedefler üzerinde çalışır ve dönem "evaluation" fazındayken de (kapı-2
// uyarısından sonra çalışanın kendini düzeltmesi için) açık kalmalıdır.
export const GOAL_WRITING_OPEN_STATUSES = ['active', 'evaluation'];

// Plan 3, Task 8 — "Ağırlık toplamı" bandı: AĞIRLIK KÜMESİ backend tanımıyla
// (Task 3 CheckWeightSumGateAsync) AYNı hesaplanır. FE tarafı bu görevin
// brief'inde açıkça istendiği gibi "listeden" türetilir — yani MyGoalsPage'in
// DataTable için zaten YÜKLEDİĞİ (sayfalanmış) `goals` dizisinden;
// ObPeriodApprovalsPage'in PendingBanner'ı gibi ayrı bir agregasyon ucu
// AÇILMADI. Bu, dönem başına hedef sayısı sayfa boyutunu (10) aşarsa bandın
// eksik sayabileceği bilinen bir sınırlamadır (bkz. task-8-report.md) —
// yalnız YÜKLENMİŞ/listelenen hedefler üzerinden hesaplanır.
//
// Final review Critical fix (F1.3, 2026-08-07) — backend kapı-3 tanımı closed
// hedefleri de kapsayacak şekilde düzeltildi (bkz. PeriodGoalDataAccess
// .CheckWeightSumGateAsync yorumu): closed-hariç eski tanımda ardışık kapanış
// (60+40, A kapanınca B'nin kümesi {B}=40'a düşer) SONSUZA DEK 409 üretiyordu.
// Bant artık TÜM (closed dahil) hedefler üzerinden hesaplanır — backend'le
// AYNI paritede.
//
// null ağırlıklı hedef toplama 0 olarak KATILMAZ (backend currentSum
// semantiğiyle birebir — CheckWeightSumGateAsync'teki
// `weights.Where(w => w.HasValue).Sum(...)`) ama varlığı hasMissing
// bayrağıyla ayrıca işaretlenir: toplam tam 100 olsa bile eksik (null)
// ağırlıklı bir hedef VARSA durum GEÇERSİZDİR (kapı-1/kapı-3 "hasNull ||
// currentSum != 100" testiyle birebir aynı mantık).
// PMB-01 (2026-09-08) — TERK EDİLMİŞ hedef ağırlık kümesinin DIŞINDA: backend
// kapısı (CheckWeightSumGateAsync, `g.Status != GoalScoreRules.AbandonedStatus`)
// artık onu saymıyor; bant da saymamalı, aksi halde kullanıcı sunucunun
// gördüğünden farklı bir toplam okur. `closed` DAHİL kalmaya devam ediyor
// (yukarıdaki F1.3 sözleşmesi bozulmuyor — yalnız yeni terminal durum eleniyor).
export const inWeightSet = (goal) => goal.status !== ABANDONED_STATUS;

export const computeWeightBand = (goals) => {
  const all = (goals || []).filter(inWeightSet);
  const sum = all.reduce((acc, g) => acc + (g.weightPct != null ? Number(g.weightPct) : 0), 0);
  const hasMissing = all.some((g) => g.weightPct == null);
  const rounded = Math.round(sum * 100) / 100;
  return {
    sum: rounded,
    goalCount: all.length,
    hasMissing,
    isValid: !hasMissing && all.length > 0 && rounded === 100,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// Task 27 — Ekip Hedefleri / Onaylar (yönetici + koşullu İK kuyruğu) + dönem
// sonu yönetici puanlaması. Gerçek yönetim uçları (PeriodGoalController'dan grep
// ile doğrulanmış — brief metnindeki "revisions/{id}/approve" tekil ucu YANLIŞ,
// gerçekte manager-decision/hr-decision diye ikiye ayrılmış, MyGoalsPage'in
// üstteki yorumundaki desenle aynı sapma):
//   GET  v1/period-goals?status[in]&periodId[eq]         (İK/tüm tenant — ListAsync)
//   GET  v1/period-goals/team?status[in]&periodId[eq]    (yönetici — yalnız doğrudan raporlar)
//   GET  v1/period-goals/{id}                             (detay — KR'ler + ilerleme + yorum)
//   POST v1/period-goals/{id}/manager-decision {decision:approve|revision, note}
//   POST v1/period-goals/{id}/hr-decision       {decision:approve|revision, note}
//   POST v1/period-goals/{id}/manager-scores    {scores:[{keyResultId,score,comment}]}
//   DELETE v1/period-goals/{id}                  (İK — müşteri geri bildirimi 2026-10-28,
//                                                 yalnız DELETABLE_STATUSES)
//
// Revizyon LİSTELEME uçları (GET revisions, GET revisions/team) BE'de YOKTU —
// Task 8 yalnız CRUD/karar uçlarını (RequestRevisionAsync/ApproveRevisionAsync)
// tanımlamıştı, hiçbir GET revizyonları listelemiyordu ve PeriodGoalResponse'ta
// da revizyon bilgisi embed edilmiyordu. "Revizyon Talepleri" sekmesi bu uçlar
// olmadan inşa edilemeyeceği için Task 27 kapsamında minimal, migrationsız bir
// BE eklentisi olarak eklendi (ListAsync/GetTeamGoalsAsync çiftiyle birebir aynı
// İK-tüm-tenant/yönetici-doğrudan-raporlar ayrımını izler — bkz. backend
// PeriodGoalController.cs GetRevisions/GetTeamRevisions, PeriodGoalDataAccess.cs
// ListRevisionsAsync/GetTeamRevisionsAsync):
//   GET  v1/period-goals/revisions?status[in]             (İK — tüm tenant)
//   GET  v1/period-goals/revisions/team?status[in]        (yönetici — doğrudan raporlar)
//   GET  v1/period-goals/revisions/{id}                   (tek revizyon — derin bağlantı, 2026-10-28)
//   POST v1/period-goals/revisions/{id}/manager-decision {decision:approve|reject, note}
//   POST v1/period-goals/revisions/{id}/hr-decision       {decision:approve|reject, note}
// Aynı fırsatla PeriodGoalResponse'a EmployeeName eklendi (MyGoalsPage'in
// ihtiyacı yoktu — yalnız kendi hedefi; bu sayfa ekip/tüm-tenant listelediği için
// ham EmployeeId (Guid) göstermek yerine bir isme ihtiyaç duyuyor).

// Sekmelerin (Onay Bekleyenler/Ekip Hedefleri/Dönem Sonu/Revizyon Talepleri)
// HER biri kendi bağımsız DataTable sayfalama/filtre state'ine sahip — tek bir
// jenerik "GET url + params → {data,fetching,pagination,refresh}" hook'u
// hepsinde tekrar kullanılır (MyGoalsPage'deki "goals" bloğunun genellenmiş hali).
export const useApiPagedList = (url, params, active = true) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (!active || !url) {
      setData([]);
      return;
    }
    setFetching(true);
    api.get(url, { params })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
    // paramsKey bilinçli bağımlılık: params her render'da yeni referans olabilir
    // (sayfa bileşeninde inline nesne) — içerik değişmediyse yeniden istek atılmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, paramsKey, active, refreshTrigger]);

  return { data, fetching, pagination, refresh };
};

// "Ekip Hedefleri" ve "Dönem Sonu" sekmelerinin dönem seçicisi — MyGoalsPage'in
// fetchPeriods'ıyla AYNI küme (PMF-10: tek kaynak fetchGoalVisiblePeriods; iki
// ekranın listesi ayrışırsa yönetici çalışanın gördüğü dönemi göremez).
export const useManagedPeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  const fetchPeriods = useCallback(async () => {
    setPeriodsFetching(true);
    try {
      setPeriods(await fetchGoalVisiblePeriods());
    } catch {
      setPeriods([]);
    } finally {
      setPeriodsFetching(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  return { periods, periodsFetching };
};

// Karar/puanlama/detay aksiyonları — tüm sekmelerde ortak (state'siz, yalnız
// çağrılabilir fonksiyonlar).
export const useTeamGoalActions = () => {
  const getById = useCallback(async (id) => {
    try {
      const res = await api.get(`/v1/period-goals/${id}`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const managerDecide = useCallback(async (id, decision, note) => {
    try {
      await api.post(`/v1/period-goals/${id}/manager-decision`, { decision, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  const hrDecide = useCallback(async (id, decision, note) => {
    try {
      await api.post(`/v1/period-goals/${id}/hr-decision`, { decision, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  // Plan 3, Task 3 kapı-3 (performance.goal.weightSumInvalid, 409, Data:
  // {currentSum, goalCount}) bu uçtan da dönebilir. Diğer tüm useTeamGoalActions
  // fonksiyonları (managerDecide/hrDecide/decideRevision*) sade boolean döner —
  // burada PeriodEndScoringModal'ın MODAL İÇİ hata satırında currentSum'ı
  // göstermesi gerektiği için (brief: "toast DEĞİL, mevcut modal hata desenine
  // bak") sözleşme genişletildi: {ok, gateError}. gateError yalnız
  // weightSumInvalid'de dolu, başka her hata/başarıda null (interceptor toast'u
  // her durumda zaten anahtar metnini basıyor — bu yalnız EK bağlam).
  const submitManagerScores = useCallback(async (id, scores) => {
    try {
      await api.post(`/v1/period-goals/${id}/manager-scores`, { scores });
      return { ok: true, gateError: null };
    } catch (err) {
      const gateError = err.response?.data?.message === 'performance.goal.weightSumInvalid'
        ? (err.response.data.data || null)
        : null;
      return { ok: false, gateError };
    }
  }, []);

  // ═══════ PMF-11 (Faz 2, 2026-09-11) — KPI HEDEFİ İÇİN YÖNETİCİ YÜZEYİ YOKTU ═══════
  //
  // Backend ucu HER ZAMAN vardı: POST v1/period-goals/{id}/kpi-final
  // (PeriodGoalController.SubmitFinalKpi → SubmitFinalKpiAsync(isManager: true),
  // kapsam kapısı CheckManagerDecisionScopeAsync). Ön yüzde ÇAĞIRAN YOKTU —
  // yalnız self-servis ikizi (my/{id}/kpi-final) çağrılıyordu.
  //
  // Sonuç: sahibi uzun süreli izinde/ayrılmış bir KPI hedefinin gerçekleşen
  // değerini kimse giremiyordu. "Dönem Sonu" sekmesindeki modal KPI dalında
  // salt-okunurdu ve gönder butonu yalnız OKR için çiziliyordu; yönetici hedefi
  // ne puanlayabiliyor ne kapatabiliyordu. Dönem kapanışı hedefi zorla kapatır
  // (PMB-01 kaskadı) ama gerçekleşen değer BOŞ kaldığı için System A skoru
  // eksik hesaplanıyordu.
  //
  // Dönüş sözleşmesi submitManagerScores ile AYNI ({ok, gateError}) — modal
  // içi hata satırı deseni değişmesin diye.
  const submitTeamFinalKpi = useCallback(async (id, value, deviationRationale) => {
    try {
      await api.post(`/v1/period-goals/${id}/kpi-final`, { value, deviationRationale });
      return { ok: true, gateError: null };
    } catch (err) {
      const gateError = err.response?.data?.message === 'performance.goal.weightSumInvalid'
        ? (err.response.data.data || null)
        : null;
      return { ok: false, gateError };
    }
  }, []);

  const decideRevisionManager = useCallback(async (revisionId, decision, note) => {
    try {
      await api.post(`/v1/period-goals/revisions/${revisionId}/manager-decision`, { decision, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  const decideRevisionHr = useCallback(async (revisionId, decision, note) => {
    try {
      await api.post(`/v1/period-goals/revisions/${revisionId}/hr-decision`, { decision, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  // P3 (2026-10-08) — yöneticinin kilometre taşı adım kararı: decision accept|reject (ret
  // gerekçesi zorunlu). Kapı backend'de (CheckManagerDecisionScopeAsync — doğrudan rapor ∪
  // vekâlet ∪ İK). Boolean sözleşme (managerDecide ile aynı).
  const decideMilestoneStep = useCallback(async (goalId, stepId, decision, note) => {
    try {
      await api.post(`/v1/period-goals/${goalId}/milestone-steps/${stepId}/decision`, { decision, note: note || undefined });
      return true;
    } catch {
      return false;
    }
  }, []);

  // P3 — eşik/ikili hedefin dönem sonu sonucunu yönetici girer (submitTeamFinalKpi ikizi —
  // sahibi izinli/ayrılmış hedef kapanabilsin). Dönüş {ok, gateError} — modal sözleşmesi aynı.
  const submitTeamFinalThreshold = useCallback(async (goalId, payload) => {
    try {
      await api.post(`/v1/period-goals/${goalId}/threshold-final`, payload);
      return { ok: true, gateError: null };
    } catch (err) {
      const gateError = err.response?.data?.message === 'performance.goal.weightSumInvalid'
        ? (err.response.data.data || null)
        : null;
      return { ok: false, gateError };
    }
  }, []);

  // Müşteri geri bildirimi (2026-10-28) — tek revizyon (derin bağlantı). GET reddetmez
  // (interceptor) — bulunamadı/kapsam dışı null döner.
  const getRevisionById = useCallback(async (revisionId) => {
    try {
      const res = await api.get(`/v1/period-goals/revisions/${revisionId}`);
      return res.data?.status === false ? null : (res.data?.data || null);
    } catch {
      return null;
    }
  }, []);

  // Müşteri geri bildirimi (2026-10-28) — İK'nın hedef silmesi (DELETE v1/period-goals/{id};
  // kapı backend'de HrActorRules.IsHrActorAsync + PM.Write). Yalnız DELETABLE_STATUSES —
  // askıda kalan (pencere dışı dönemde onayda bekleyen) ya da terk edilmiş hedefin çıkışı.
  const deleteGoal = useCallback(async (id) => {
    try {
      await api.delete(`/v1/period-goals/${id}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  return {
    getById, managerDecide, hrDecide, submitManagerScores, submitTeamFinalKpi,
    decideRevisionManager, decideRevisionHr, decideMilestoneStep, submitTeamFinalThreshold,
    deleteGoal, getRevisionById,
  };
};

// Onay Akışları > Hedef (2026-10-28) — "Onay Bekleyenler" satırında karar eylemi: sayfanın
// yönetici kuralı (writable — PM.Write ya da astı var) YA DA satır aktörün ONAYCISI olduğu akış
// adımı (inApprovalFlow — kuyruğun akış dalı yalnız aktörün kapsamındaki adımları getirir; astı/
// PM.Write'ı olmayan akış onaycısı da karar verebilmeli). Skip-level satırda hiç açılmaz.
export const canDecidePendingGoalRow = (row, { writable }) => {
  if (!row || row.isSkipLevel) return false;
  return Boolean(writable || row.inApprovalFlow);
};

// Müşteri geri bildirimi (2026-10-28) — gelen kutusundan derin bağlantı (?goal=<id>):
// GoalDecisionModal KARAR kipinde mi salt-okunur mu açılır. Karar yalnız hedef GERÇEKTEN o
// aşamada bekliyorsa ve sayfanın kendi görünürlük kuralı o aşamanın aksiyonunu gösteriyorsa
// (yönetici: canDecideAsManager, İK: canDecideAsHr — satır menülerindeki kurallarla aynı);
// skip-level satırda karar hiç açılmaz (pendingColumns ile aynı DTO-driven guard). Hedef
// bulunamadıysa (null) modal salt-okunur açılır ve kendi "bulunamadı" mesajını gösterir.
// Onay Akışları > Hedef (2026-10-28): akış adımında bekleyen hedefte (inApprovalFlow) yönetici
// kuralı GEÇMEZ — karar adımın onaycısınındır (GET {id}'nin viewerIsFlowApprover'ı) ya da İK'nın
// (backend İK fallback'i); doğrudan yönetici adımla eşleşmiyorsa backend 403 verirdi.
export const resolveGoalDeepLinkMode = (goal, { canDecideAsManager, canDecideAsHr }) => {
  if (!goal || goal.isSkipLevel) return { isHr: false, readOnly: true };
  if (goal.status === 'manager_approval') {
    const canDecide = goal.inApprovalFlow
      ? Boolean(goal.viewerIsFlowApprover || canDecideAsHr)
      : Boolean(canDecideAsManager);
    return canDecide ? { isHr: false, readOnly: false } : { isHr: false, readOnly: true };
  }
  if (goal.status === 'hr_approval' && canDecideAsHr) return { isHr: true, readOnly: false };
  return { isHr: false, readOnly: true };
};

// Müşteri geri bildirimi (2026-10-28) — derin bağlantı (?tab=revisions&revision=<id>): revizyon
// tekil GET ile yüklenir (GET v1/period-goals/revisions/{id} — kapsam iki liste ucunun birleşimi).
// Karar modalı yalnız talep o aşamada bekliyor VE sayfa o aşamanın aksiyonunu gösteriyorsa açılır:
// yönetici aşaması → 'team' kapsamı (writable), İK aşaması → 'all' kapsamı (hrRevisionsVisible).
// Karar verilmiş (applied/rejected) ya da aksiyonu görünmeyen talepte null (modal açılmaz).
export const resolveRevisionDeepLinkMode = (revision, { canDecideAsManager, canDecideAsHr }) => {
  if (!revision) return null;
  if (revision.status === 'manager_approval' && canDecideAsManager) return { scope: 'team', isHr: false };
  if (revision.status === 'hr_approval' && canDecideAsHr) return { scope: 'all', isHr: true };
  return null;
};

// P3 — yöneticinin "Adım Onayları" kuyruğu (kabul bekleyen kilometre taşı adımları).
export const PENDING_STEPS_URL = '/v1/period-goals/milestone-steps/pending';

export const buildPendingStepColumns = (t, data) => [
  { name: 'stepId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
  {
    name: 'employeeName',
    label: t('performance.goal.fields.employee'),
    options: { filter: false, sort: false },
  },
  {
    name: 'goalTitle',
    label: t('performance.goal.fields.title'),
    options: { filter: false, sort: false },
  },
  {
    name: 'stepTitle',
    label: t('performance.goal.milestone.step'),
    options: { filter: false, sort: false },
  },
  {
    name: 'weightPct',
    label: t('performance.goal.milestone.stepWeight'),
    options: {
      filter: false, sort: false,
      customBodyRender: (v) => (v != null ? `${v}%` : '-'),
    },
  },
  {
    name: 'submittedAt',
    label: t('performance.goal.milestone.submittedAt'),
    options: {
      filter: false, sort: false,
      customBodyRender: (_, tableMeta) => {
        const row = data[tableMeta.rowIndex];
        return row?.submittedAt ? String(row.submittedAt).slice(0, 10) : '-';
      },
    },
  },
];

// OKR: aktif KR'ların ortalama ilerlemesi. KPI: zaten hesaplı CompletionPct.
// Her ikisi de yoksa (KR'siz OKR — teoride olmamalı) "-" döner.
export const computeGoalProgressPct = (goal) => {
  if (!goal) return null;
  if (goal.goalType === 'kpi') return goal.completionPct;
  // P3 — kilometre taşı: kabul edilen adım ağırlığı oranı; eşik/ikili: 100/0 (sonuç yoksa '-').
  if (goal.goalType === 'milestone') return goal.milestoneProgressPct ?? null;
  if (goal.goalType === 'threshold') return goal.completionPct ?? null;
  const krs = goal.keyResults || [];
  if (krs.length === 0) return null;
  const sum = krs.reduce((acc, k) => acc + (Number(k.progressPct) || 0), 0);
  return Math.round((sum / krs.length) * 100) / 100;
};

// Plan 3, Task 8 fix (inceleme R1) — çalışan-grup ağırlık toplamı. İLK sürüm bu
// toplamı SEKMENİN durum-filtreli satırlarından (ör. yalnız status[in]=
// manager_approval) türetiyordu — bu YANLIŞTI: hedefler tek tek submit edildiği
// için bir çalışanın hedefleri rutin olarak draft/manager_approval/approved
// arasında BÖLÜNÜR, bu yüzden "Onay Bekleyenler" sekmesindeki satırlardan
// hesaplanan bir toplam SİSTEMATİK OLARAK eksik sayardı (kenar durum değil,
// rutin — bkz. task-8-report.md fix bölümü). Dürüst kaynak: dönemin FİLTRESİZ
// takım hedefleri — GET /v1/period-goals/team, status[in] YOK, periodId[eq],
// pageSize 200 (yalnız bu toplam için; sayfa DataTable'larından BAĞIMSIZ, ayrı
// bir istek — TeamGoalsPage selectedPeriodId değiştiğinde BİR kez çağırır).
//
// Dönen map: { [employeeName]: { sum, hasMissing } } — null 0 sayılmaz
// (computeWeightBand ile AYNI semantik). `complete: false` ise (yanıtın
// totalRowCount'u döndürülen öğe sayısını AŞTIYSA — pageSize 200 sınırı fiilen
// aşıldı, pratikte olmaz) harita GÜVENİLMEZ sayılır — çağıran bu durumda YANLIŞ
// bir mutlak toplam iddia ETMEMELİ (bkz. buildTeamGoalColumns'ta kullanımı).
//
// Final review Critical fix (F1.3, 2026-08-07) — backend kapı-3 tanımı closed
// hedefleri de kapsayacak şekilde düzeltildi (CheckWeightSumGateAsync); bu
// toplam artık BE ile paritede closed hedefleri de sayar (eski `status ===
// 'closed'` filtresi kaldırıldı).
//
// Final review Important fix (F2, 2026-08-07) — grup anahtarı artık
// employeeId DEĞİL, employeeName: PeriodGoalDataAccess.ResolveOwnerIdentitiesAsync
// (B8 fallback) kimlik-bölünmüş bir çalışanın hedeflerini BE kapısında TEK
// kümede toplarken, bu FE toplamı ham employeeId ile gruplayınca aynı çalışan
// İKİ ayrı kısmi satır olarak görünüyor ve BE kapısı 100/100 ile geçerken bu
// tooltip sahte ⚠ üretiyordu. employeeName pratik bir risk taşır: yöneticinin
// DOĞRUDAN ekibinde AYNI isimli iki farklı kişi VE bunlardan birinin
// kimlik-bölünmesi olması gerekir — bu ikilinin birlikte gerçekleşmesi
// pratikte görülmez (ekip boyutu küçük, isim çakışması nadir).
export const fetchTeamWeightTotals = async (periodId) => {
  const res = await api.get('/v1/period-goals/team', {
    params: { 'periodId[eq]': periodId, pageSize: 200, pageNumber: 1 },
  });
  const result = res.data?.data;
  const items = Array.isArray(result) ? result : (result?.data || []);
  const totalRowCount = result?.totalRowCount ?? items.length;
  const map = {};
  // PMB-01 — computeWeightBand ile AYNI küme tanımı (inWeightSet): terk edilmiş
  // hedef backend kapısının toplamına girmediği için bu tooltip toplamına da
  // girmemeli, aksi halde yönetici sahte bir ⚠ görür.
  items.filter(inWeightSet).forEach((g) => {
    const key = g.employeeName;
    const entry = map[key] || { sum: 0, hasMissing: false };
    if (g.weightPct != null) entry.sum += Number(g.weightPct);
    else entry.hasMissing = true;
    map[key] = entry;
  });
  Object.keys(map).forEach((name) => { map[name].sum = Math.round(map[name].sum * 100) / 100; });
  return { map, complete: totalRowCount <= items.length };
};

// "Ekip Hedefleri" / "Onay Bekleyenler" / "Dönem Sonu" sekmelerinin ortak temel
// kolonları (employee + title + goalType + [ağırlık] + ilerleme + status) —
// sayfa bunun üzerine aksiyon kolonu ekler. Saf fonksiyon (hook DEĞİL) —
// useMemo içinde t değiştiğinde yeniden üretilir.
//
// Plan 3, Task 8 — periodWeightFlags: { [periodId]: usesGoalWeights } haritası
// (PeriodGoalResponse satırları period bilgisini EMBED ETMİYOR — yalnız çıplak
// PeriodId — bkz. task-8-report.md; sayfa bunu kendi `periods` listesinden
// kurar). Ağırlık kolonu yalnız görünen veri kümesinde EN AZ BİR satırın dönemi
// bayraklıysa eklenir (parite: hiç bayraklı dönem yoksa kolon HİÇ render
// edilmez); bayraksız bir dönemin satırında hücre "-" gösterir.
//
// Plan 3, Task 8 fix (inceleme R1) — teamWeightTotals: { periodId, map, complete }
// | null, fetchTeamWeightTotals'ın (bkz. yukarısı) sonucu + hangi periodId için
// çekildiği. Yalnız "Onay Bekleyenler"/"Dönem Sonu" sekmelerinde geçirilir
// (brief'in bu iki sekmeye özel istediği çalışan-grup göstergesi) — "Ekip
// Hedefleri" sekmesinde tek dönem zaten seçili olduğu ve liste o dönemin TÜM
// durumlarını kapsadığı için ek tooltip'e ihtiyaç yok (bkz. task-8-report.md
// yerleşim kararı, bu fix'te DEĞİŞMEDİ). Hücre yalnız satırın periodId'si
// teamWeightTotals.periodId ile eşleşiyorsa VE harita complete=true ise
// tooltip'i gösterir — aksi halde (farklı dönem, eksik/erken veri, ya da
// teamWeightTotals hiç geçirilmediyse) YANLIŞ bir mutlak toplam iddia etmemek
// için düz (grup bilgisiz) hücreye düşer.
export const buildTeamGoalColumns = (t, data, periodWeightFlags = {}, teamWeightTotals = null) => {
  const cols = [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'employeeName',
      label: t('performance.goal.fields.employee'),
      options: {
        filter: false, sort: false,
        // Faz J (Task 4) — DTO-driven: yalnız row.isSkipLevel okunur, FE
        // hangi sekmede bu bayrağın gerçekten dolabileceğine dair bir
        // varsayım ÜRETMEZ (bkz. dosya başı SkipLevelBadge yorumu).
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row?.isSkipLevel) return v;
          return (
            <>
              {v}
              <SkipLevelBadge data-role="skip-level-badge">{t('skipLevel.badge')}</SkipLevelBadge>
            </>
          );
        },
      },
    },
    {
      name: 'title',
      label: t('performance.goal.fields.title'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'goalType',
      label: t('performance.goal.fields.goalType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`performance.goal.goalType.${v}`, { defaultValue: v }),
      },
    },
  ];

  const showWeightColumn = (data || []).some((g) => periodWeightFlags[g.periodId]);
  if (showWeightColumn) {
    cols.push({
      name: 'weightPct',
      label: t('performance.goal.fields.weight'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row || !periodWeightFlags[row.periodId]) return '-';
          const own = row.weightPct != null ? `${row.weightPct}%` : '-';
          // F2 fix — harita artık employeeName ile anahtarlanıyor (bkz.
          // fetchTeamWeightTotals yorumu, kimlik-bölünmüş çalışanın split
          // satırlarını doğru birleştirmek için).
          const totals = teamWeightTotals
            && teamWeightTotals.complete
            && teamWeightTotals.periodId === row.periodId
            ? teamWeightTotals.map[row.employeeName]
            : null;
          if (!totals) return <span data-role="weight-cell">{own}</span>;
          const groupLabel = `${totals.sum}/100${totals.hasMissing ? ' ⚠' : ''}`;
          return (
            <Tooltip text={`${row.employeeName}: ${groupLabel}`}>
              <span data-role="weight-cell">{own}</span>
            </Tooltip>
          );
        },
      },
    });
  }

  cols.push(
    {
      name: 'progressPct',
      label: t('performance.teamGoals.progress'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const pct = computeGoalProgressPct(row);
          return pct != null ? `${pct}%` : '-';
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: false, sort: false,
        // Skorlama Faz F, Task 5 — bayat rozet, weightPct hücresindeki
        // `data[tableMeta.rowIndex]` deseninin BİREBİR aynısı.
        customBodyRender: (v, tableMeta) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          const row = data[tableMeta.rowIndex];
          const staleDays = row?.isStale ? staleDaysSince(row.lastProgressAt) : null;
          return (
            <>
              <StatusBadge color={cfg.color} bg={cfg.bg}>
                {t(`performance.goal.status.${v}`, { defaultValue: v })}
              </StatusBadge>
              {staleDays != null && (
                <StaleBadge data-role="stale-badge">
                  {t('staleGoals.badge', { days: staleDays })}
                </StaleBadge>
              )}
            </>
          );
        },
      },
    },
  );

  return cols;
};

// Revizyon durumları hedefin kendi 7 durumundan BAĞIMSIZ, ayrı bir küme
// (manager_approval|hr_approval|applied|rejected) — "applied"/"rejected" hedefin
// STATUS_COLORS/status çevirisinde yok, bu yüzden ayrı bir palet+i18n anahtarı.
export const REVISION_STATUS_COLORS = {
  manager_approval: { color: '#D97706', bg: '#FFFBEB' },
  hr_approval: { color: '#D97706', bg: '#FFFBEB' },
  applied: { color: '#16A34A', bg: '#DCFCE7' },
  rejected: { color: '#B02A37', bg: '#F8D7DA' },
};

export const buildRevisionColumns = (t) => [
  { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
  {
    name: 'employeeName',
    label: t('performance.goal.fields.employee'),
    options: { filter: false, sort: false },
  },
  {
    name: 'goalTitle',
    label: t('performance.goal.fields.title'),
    options: { filter: false, sort: false },
  },
  {
    name: 'status',
    label: t('common.status'),
    options: {
      filter: false, sort: false,
      customBodyRender: (v) => {
        const cfg = REVISION_STATUS_COLORS[v] || REVISION_STATUS_COLORS.manager_approval;
        return (
          <StatusBadge color={cfg.color} bg={cfg.bg}>
            {t(`performance.teamGoals.revisionStatus.${v}`, { defaultValue: v })}
          </StatusBadge>
        );
      },
    },
  },
];

export default usePeriodGoalsApi;
